/**
 * Keyboard / AT support for header overlays: desktop mega/sub/account menus,
 * mobile nav, search, and mini cart. Complements _sm-theme.js (hover + click
 * toggles) with disclosure activation, Escape dismiss, focus trap, and
 * aria-expanded sync — without opening panels on focus alone.
 */
;(function () {
  var desktopBreakpoint = 981
  var focusableSelector =
    'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'

  var activePanel = null

  function isDesktop() {
    return window.innerWidth >= desktopBreakpoint
  }

  function isVisible(el) {
    if (!el || el.disabled) return false
    if (el.getAttribute('aria-hidden') === 'true') return false
    if (el.closest && el.closest('[inert]')) return false
    var style = window.getComputedStyle(el)
    if (style.visibility === 'hidden' || style.display === 'none') return false
    // Avoid checkVisibility({checkOpacity}) — open submenus animate opacity,
    // which would drop links mid-animation and reshuffle the trap list.
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
  }

  // Skynet "All in One Accessibility" (and similar overlays) slap tabindex="0"
  // onto .sub-nav-links > li. That makes each item two tab stops (li then a),
  // which feels like walking the submenu twice. Strip those and keep them off.
  function stripSubnavListTabStops(root) {
    var scope = root || document
    scope
      .querySelectorAll(
        '.sub-nav-links li[tabindex], .sub-nav-links li [tabindex], .accordion-content-inner li[tabindex]'
      )
      .forEach(function (el) {
        if (el.tagName === 'A' || el.tagName === 'BUTTON') return
        if (el.classList && el.classList.contains('accordion-toggle')) return
        el.removeAttribute('tabindex')
      })
  }

  function observeSubnavTabStopStrip(nav) {
    if (!nav || nav.dataset.iwSubnavTabStrip === '1') return
    nav.dataset.iwSubnavTabStrip = '1'
    stripSubnavListTabStops(nav)
    var timer = null
    var observer = new MutationObserver(function () {
      window.clearTimeout(timer)
      timer = window.setTimeout(function () {
        stripSubnavListTabStops(nav)
      }, 50)
    })
    observer.observe(nav, {
      attributes: true,
      attributeFilter: ['tabindex'],
      subtree: true,
      childList: true
    })
  }

  function resolveSubnavLeaf(el, subLinks) {
    if (!el) return null
    if (subLinks.indexOf(el) !== -1) return el
    var leaf = el.closest && el.closest('a[href], button')
    if (leaf && subLinks.indexOf(leaf) !== -1) return leaf
    if (el.querySelector) {
      leaf = el.querySelector('a[href], button')
      if (leaf && subLinks.indexOf(leaf) !== -1) return leaf
    }
    return null
  }

  function isOpenAccordionPanel(panel) {
    if (!panel) return false
    if (panel.hasAttribute('inert')) return false
    if (panel.classList.contains('active')) return true
    var li = panel.parentElement
    if (li && li.classList.contains('active')) return true
    var toggle =
      li &&
      li.querySelector(
        ':scope > .accordion-toggle.active, :scope > button.accordion-toggle.active'
      )
    return !!toggle
  }

  function getSubnavPanelLinks(panel) {
    if (!panel) return []
    stripSubnavListTabStops(panel)
    var seen = Object.create(null)
    return Array.prototype.slice
      .call(panel.querySelectorAll('a[href]:not([tabindex="-1"])'))
      .filter(function (el) {
        if (!isVisible(el)) return false
        var key =
          (el.getAttribute('href') || '').trim() +
          '|' +
          (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase()
        if (!key || key.charAt(0) === '|' || seen[key]) return false
        seen[key] = true
        return true
      })
  }

  function getOpenMobileAccordionLi(fromEl) {
    if (!fromEl || !fromEl.closest) return null
    var li = fromEl.closest('li.has-sub-nav, li.has-big-nav')
    if (!li) return null
    var toggle = li.querySelector(
      ':scope > .accordion-toggle, :scope > button.site-nav__link'
    )
    if (
      li.classList.contains('active') ||
      (toggle && toggle.classList.contains('active'))
    ) {
      return li
    }
    return null
  }

  function getOpenMobileAccordionPanel(openLi) {
    if (!openLi) return null
    return openLi.querySelector(
      ':scope > .sub-nav.accordion-content, :scope > .big-nav.accordion-content, :scope > .accordion-content'
    )
  }

  function focusNextMobileTopLevel(openLi, direction) {
    if (!openLi || !openLi.parentElement) return null
    var items = Array.prototype.filter.call(
      openLi.parentElement.children,
      function (el) {
        return el.tagName === 'LI'
      }
    )
    var idx = items.indexOf(openLi)
    if (idx === -1) return null
    var step = direction < 0 ? -1 : 1
    for (var i = idx + step; i >= 0 && i < items.length; i += step) {
      var candidate = items[i]
      var control = candidate.querySelector(
        ':scope > .accordion-toggle, :scope > button.site-nav__link, :scope > a[href]'
      )
      if (control && isVisible(control)) return control
    }
    return null
  }

  function getFocusables(container, extras) {
    stripSubnavListTabStops(container)
    var nodes = Array.prototype.slice.call(
      container.querySelectorAll(focusableSelector)
    )

    // Mobile drawer also mounts the desktop nav list (hidden via CSS).
    // Only strip it when collecting for a non-desktop container — otherwise
    // openDesktopMenu's panel (inside .desktop-menu-main) loses every link
    // and Tab exits straight to the next top-level item.
    if (!container.closest || !container.closest('.desktop-menu-main')) {
      nodes = nodes.filter(function (el) {
        return !el.closest('.desktop-menu-main')
      })
    }

    // Submenu list items must never be tab stops — only their links/buttons.
    nodes = nodes.filter(function (el) {
      if (!el.closest('.sub-nav-links, .accordion-content-inner')) return true
      return el.tagName === 'A' || el.tagName === 'BUTTON'
    })

    // Accordion panels: only while open.
    nodes = nodes.filter(function (el) {
      var panel = el.closest(
        'li > .accordion-content, li > .big-nav, li > .sub-nav'
      )
      if (!panel) return true
      return isOpenAccordionPanel(panel)
    })

    // Prefer leaf controls over focusable wrappers.
    nodes = nodes.filter(function (el) {
      return !nodes.some(function (other) {
        return other !== el && el.contains(other)
      })
    })

    // Collapse each open submenu to a single deduped link list in place.
    var openPanels = container.querySelectorAll(
      'li.active > .sub-nav.accordion-content, li.active > .big-nav.accordion-content, li.active > .accordion-content'
    )
    if (openPanels.length) {
      var rebuilt = []
      var handled = []
      nodes.forEach(function (el) {
        var panel = null
        for (var i = 0; i < openPanels.length; i++) {
          if (openPanels[i].contains(el)) {
            panel = openPanels[i]
            break
          }
        }
        if (!panel) {
          rebuilt.push(el)
          return
        }
        if (handled.indexOf(panel) !== -1) return
        handled.push(panel)
        getSubnavPanelLinks(panel).forEach(function (link) {
          rebuilt.push(link)
        })
      })
      nodes = rebuilt
    }

    if (extras && extras.length) {
      extras.forEach(function (el) {
        if (el && nodes.indexOf(el) === -1) nodes.unshift(el)
      })
    }
    return nodes.filter(isVisible)
  }

  function releasePanel(restoreFocus) {
    if (!activePanel) return
    var panel = activePanel
    activePanel = null
    document.removeEventListener('keydown', onDocumentKeydown, true)
    if (typeof panel.onClose === 'function') {
      panel.onClose()
    }
    if (
      restoreFocus !== false &&
      panel.trigger &&
      typeof panel.trigger.focus === 'function'
    ) {
      try {
        panel.trigger.focus()
      } catch (err) {
        /* detached */
      }
    }
  }

  function activatePanel(options) {
    if (
      activePanel &&
      activePanel.container === options.container &&
      activePanel.id === options.id
    ) {
      return
    }
    releasePanel(false)
    activePanel = {
      id: options.id,
      container: options.container,
      trigger: options.trigger || document.activeElement,
      extras: options.extras || [],
      onClose: options.onClose || null,
      keyboardTrap: options.keyboardTrap !== false
    }
    document.addEventListener('keydown', onDocumentKeydown, true)
    var focusTarget =
      options.focusEl ||
      getFocusables(options.container, activePanel.extras)[0] ||
      options.container
    if (focusTarget && typeof focusTarget.focus === 'function') {
      if (focusTarget === options.container) {
        options.container.setAttribute('tabindex', '-1')
      }
      window.setTimeout(function () {
        try {
          focusTarget.focus()
        } catch (err) {
          /* ignore */
        }
      }, 10)
    }
  }

  function isInActivePanel(el) {
    if (!activePanel || !el) return false
    if (activePanel.container.contains(el)) return true
    return activePanel.extras.indexOf(el) !== -1
  }

  function getPageFocusables() {
    return Array.prototype.slice
      .call(document.querySelectorAll(focusableSelector))
      .filter(isVisible)
  }

  function findAdjacentFocusable(fromEl, direction) {
    if (!fromEl) return null
    var all = getPageFocusables()
    var idx = all.indexOf(fromEl)
    if (idx === -1) return null
    return all[idx + direction] || null
  }

  function onDocumentKeydown(e) {
    if (!activePanel) return

    if (e.key === 'Escape') {
      e.preventDefault()
      dismissActivePanel()
      return
    }

    if (e.key !== 'Tab' || !activePanel.keyboardTrap) return

    var focusables = getFocusables(activePanel.container, activePanel.extras)
    if (!focusables.length) {
      e.preventDefault()
      return
    }
    var first = focusables[0]
    var last = focusables[focusables.length - 1]
    var active = document.activeElement

    // Desktop mega/account menus: Tab past the last item (or Shift+Tab
    // before the trigger) closes the disclosure like Escape, then moves
    // focus to the next/previous page control — no wrap back into the panel.
    if (activePanel.id === 'desktop-menu') {
      var idx = focusables.indexOf(active)
      if (!e.shiftKey) {
        if (active === last || !isInActivePanel(active)) {
          e.preventDefault()
          exitDesktopMenu(1)
          return
        }
        if (idx >= 0 && idx < focusables.length - 1) {
          e.preventDefault()
          focusables[idx + 1].focus()
        }
        return
      }
      if (active === first || !isInActivePanel(active)) {
        e.preventDefault()
        exitDesktopMenu(-1)
        return
      }
      if (idx > 0) {
        e.preventDefault()
        focusables[idx - 1].focus()
      }
      return
    }

    // Mobile nav: walk an open accordion submenu once, then jump to the next
    // top-level item (DOM sibling) — never wrap to hamburger and back through
    // the same links again before advancing.
    if (activePanel.id === 'mobile-nav' && active) {
      var openLi = getOpenMobileAccordionLi(active)
      if (openLi) {
        var openPanel = getOpenMobileAccordionPanel(openLi)
        var openToggle = openLi.querySelector(
          ':scope > .accordion-toggle, :scope > button.site-nav__link'
        )
        var onToggle =
          openToggle && (active === openToggle || openToggle.contains(active))
        var inPanel = openPanel && openPanel.contains(active)

        if (onToggle || inPanel) {
          var subLinks = getSubnavPanelLinks(openPanel)
          e.preventDefault()
          if (!e.shiftKey) {
            if (onToggle) {
              if (subLinks.length) subLinks[0].focus()
              else {
                var skipNext = focusNextMobileTopLevel(openLi, 1)
                if (skipNext) skipNext.focus()
                else first.focus()
              }
              return
            }
            var leaf = resolveSubnavLeaf(active, subLinks)
            var subIdx = leaf ? subLinks.indexOf(leaf) : -1
            if (subIdx >= 0 && subIdx < subLinks.length - 1) {
              subLinks[subIdx + 1].focus()
            } else {
              var nextTop = focusNextMobileTopLevel(openLi, 1)
              if (nextTop) nextTop.focus()
              else if (last && !openLi.contains(last)) last.focus()
              else first.focus()
            }
            return
          }
          if (onToggle) {
            var prevTop = focusNextMobileTopLevel(openLi, -1)
            if (prevTop) prevTop.focus()
            else last.focus()
            return
          }
          var shiftLeaf = resolveSubnavLeaf(active, subLinks)
          var shiftIdx = shiftLeaf ? subLinks.indexOf(shiftLeaf) : -1
          if (shiftIdx > 0) {
            subLinks[shiftIdx - 1].focus()
          } else if (openToggle) {
            openToggle.focus()
          } else {
            var prevTop2 = focusNextMobileTopLevel(openLi, -1)
            if (prevTop2) prevTop2.focus()
            else last.focus()
          }
          return
        }
      }
    }

    // Full-cycle trap for overlays (mobile nav, search, cart): always step
    // within the collected focusables. Relying on native Tab order breaks for
    // mobile nav because #hamburger sits outside #navbarNavDropdown in the DOM
    // (search/account/cart sit between them), so focus escapes and then snaps
    // back to the hamburger — a 2-stop loop that never re-enters the menu.
    var trapIdx = focusables.indexOf(active)
    e.preventDefault()
    if (e.shiftKey) {
      if (trapIdx > 0) {
        focusables[trapIdx - 1].focus()
      } else {
        last.focus()
      }
    } else if (trapIdx >= 0 && trapIdx < focusables.length - 1) {
      focusables[trapIdx + 1].focus()
    } else {
      first.focus()
    }
  }

  function exitDesktopMenu(direction) {
    if (!activePanel || activePanel.id !== 'desktop-menu') return
    var trigger = activePanel.trigger
    activePanel.onClose = null
    closeDesktopMenus()
    releasePanel(false)
    var target = findAdjacentFocusable(trigger, direction)
    if (target && typeof target.focus === 'function') {
      try {
        target.focus()
      } catch (err) {
        /* ignore */
      }
    } else if (trigger && typeof trigger.focus === 'function') {
      try {
        trigger.focus()
      } catch (err2) {
        /* ignore */
      }
    }
  }

  function closeMobileAccordion(toggle) {
    if (!toggle || !toggle.classList.contains('active')) return false
    var content =
      toggle.parentElement &&
      toggle.parentElement.querySelector(
        ':scope > .accordion-content, :scope > .big-nav, :scope > .sub-nav'
      )
    toggle.classList.remove('active')
    toggle.setAttribute('aria-expanded', 'false')
    var parentLi = toggle.closest('li.has-sub-nav, li.has-big-nav')
    if (parentLi) parentLi.classList.remove('active')
    if (content) {
      content.style.display = 'none'
      content.style.removeProperty('height')
      content.style.removeProperty('padding-top')
      content.style.removeProperty('padding-bottom')
      content.style.removeProperty('margin-top')
      content.style.removeProperty('margin-bottom')
      setAccordionPanelInert(content, true)
    }
    return true
  }

  function dismissActivePanel() {
    if (!activePanel) return
    var id = activePanel.id
    var trigger = activePanel.trigger
    activePanel.onClose = null

    if (id === 'mobile-nav') {
      // Progressive Esc: collapse the open accordion first, keep the menu trap.
      var nav = document.getElementById('navbarNavDropdown')
      var openToggle =
        (nav && nav.querySelector('.accordion-toggle.active')) || null
      if (openToggle && closeMobileAccordion(openToggle)) {
        try {
          openToggle.focus()
        } catch (err) {
          /* ignore */
        }
        return
      }

      var hamburger = document.getElementById('hamburger')
      releasePanel(false)
      if (hamburger && hamburger.classList.contains('active')) {
        hamburger.click()
      }
      if (trigger && typeof trigger.focus === 'function') trigger.focus()
      return
    }
    if (id === 'cart') {
      releasePanel(false)
      closeCartUi()
      if (trigger && typeof trigger.focus === 'function') trigger.focus()
      return
    }
    if (id === 'search') {
      releasePanel(false)
      closeSearchUi()
      focusSearchTrigger(trigger)
      return
    }
    if (id === 'desktop-menu') {
      closeDesktopMenus()
      releasePanel(true)
      return
    }
    releasePanel(true)
  }

  function closeCartUi() {
    var closeBtn = document.querySelector('#CartSidebar.active .js__cart-close')
    if (closeBtn) {
      closeBtn.click()
      return
    }
    var sidebar = document.getElementById('CartSidebar')
    var overlay = document.getElementById('cart_overlay')
    if (sidebar) sidebar.classList.remove('active')
    if (overlay) overlay.classList.remove('active')
    document.documentElement.classList.remove('mini-cart-open')
    document.querySelectorAll('.js__cart-expand').forEach(function (btn) {
      btn.classList.remove('active')
      btn.setAttribute('aria-expanded', 'false')
    })
  }

  function closeSearchUi() {
    var closeBtns = document.querySelectorAll(
      '.iw-search.active .js-search-close'
    )
    if (closeBtns.length) {
      closeBtns.forEach(function (btn) {
        btn.click()
      })
    }
    document
      .querySelectorAll('iw-search.active, .iw-search.active')
      .forEach(function (el) {
        el.classList.remove('active')
      })
    document.querySelectorAll('.js__search').forEach(function (el) {
      el.classList.remove('active')
      el.setAttribute('aria-expanded', 'false')
    })
    var header = document.querySelector('.js__main-header')
    if (header) header.classList.remove('active')
  }

  // Prefer the control that opened search; otherwise the visible viewport
  // icon. Header has separate mobile/desktop .js__search nodes, and the
  // first in DOM is often display:none on the other breakpoint.
  function getSearchTrigger(preferred) {
    function isSearchIcon(el) {
      return (
        el &&
        el.matches &&
        el.matches('.js__search') &&
        !el.classList.contains('close-search') &&
        !(el.closest && el.closest('iw-search, .iw-search'))
      )
    }
    if (isSearchIcon(preferred) && isVisible(preferred)) return preferred
    var toggles = document.querySelectorAll('.js__search')
    for (var i = 0; i < toggles.length; i++) {
      if (isSearchIcon(toggles[i]) && isVisible(toggles[i])) return toggles[i]
    }
    if (isSearchIcon(preferred)) return preferred
    for (var j = 0; j < toggles.length; j++) {
      if (isSearchIcon(toggles[j])) return toggles[j]
    }
    return null
  }

  function focusSearchTrigger(preferred) {
    var target = getSearchTrigger(preferred)
    if (target && typeof target.focus === 'function') {
      try {
        target.focus()
      } catch (err) {
        /* ignore */
      }
    }
  }

  function getDesktopMenuPanel(item) {
    return (
      item.querySelector(':scope > .big-nav') ||
      item.querySelector(':scope > .sub-nav') ||
      item.querySelector('.big-nav, .sub-nav')
    )
  }

  function getDesktopMenuTrigger(item) {
    var accountBtn = item.querySelector(
      ':scope > button.js-account-menu-trigger'
    )
    if (accountBtn) return accountBtn

    // Prefer the desktop-visible parent link (none-980), never the
    // mobile-only accordion-toggle (block-980) when both exist.
    var desktopLink = item.querySelector(':scope > a.site-nav__link.none-980')
    if (desktopLink) return desktopLink

    var nonMobile = item.querySelector(
      ':scope > a.site-nav__link:not(.block-980), :scope > button.site-nav__link:not(.block-980)'
    )
    if (nonMobile) return nonMobile

    return item.querySelector(':scope > .site-nav__link')
  }

  function setMenuPanelInert(panel, inert) {
    if (!panel) return
    if (inert) {
      panel.setAttribute('inert', '')
    } else {
      panel.removeAttribute('inert')
    }
    // Also stamp inert on the submenu controls QA inspects (column titles +
    // leaf links). Parent inert already disables them; mirroring the attribute
    // on these nodes matches the expected open/closed DOM contract.
    panel
      .querySelectorAll(
        'a.sub-link, a.pre-title, .accordion-content-inner a, .sub-nav-links a, .big-nav-links > a'
      )
      .forEach(function (el) {
        if (inert) el.setAttribute('inert', '')
        else el.removeAttribute('inert')
      })
  }

  function setDesktopMenuOpen(item, open) {
    var panel = getDesktopMenuPanel(item)
    var trigger = getDesktopMenuTrigger(item)
    if (!panel || !trigger) return

    if (open) {
      item.classList.add('iw-menu-open')
      trigger.classList.add('hover-submenu')
      trigger.setAttribute('aria-expanded', 'true')
      panel.classList.add('active')
      panel.style.visibility = 'visible'
      panel.style.opacity = '1'
      panel.style.zIndex = '1'
      if (isDesktop()) setMenuPanelInert(panel, false)
      document.body.classList.remove('transparent-header')
      var mainHeader = document.querySelector('.main-header')
      if (mainHeader) mainHeader.classList.add('active')
    } else {
      item.classList.remove('iw-menu-open')
      trigger.classList.remove('hover-submenu')
      trigger.setAttribute('aria-expanded', 'false')
      panel.classList.remove('active')
      panel.style.visibility = ''
      panel.style.opacity = ''
      panel.style.zIndex = ''
      if (isDesktop()) setMenuPanelInert(panel, true)
    }
  }

  function closeDesktopMenus(except) {
    document
      .querySelectorAll('.has-sub-nav, .has-big-nav')
      .forEach(function (item) {
        if (except && item === except) return
        var panel = getDesktopMenuPanel(item)
        var isOpen =
          item.classList.contains('iw-menu-open') ||
          (panel && panel.classList.contains('active'))
        if (isOpen) setDesktopMenuOpen(item, false)
      })
    if (!document.querySelector('.iw-menu-open')) {
      var header = document.querySelector('.main-header')
      var searchOpen = document.querySelector('.iw-search.active')
      if (header && !searchOpen) header.classList.remove('active')
    }
  }

  function openDesktopMenu(item, trigger) {
    closeDesktopMenus(item)
    setDesktopMenuOpen(item, true)
    var panel = getDesktopMenuPanel(item)
    activatePanel({
      id: 'desktop-menu',
      container: panel,
      trigger: trigger,
      extras: [trigger],
      keyboardTrap: true,
      onClose: function () {
        setDesktopMenuOpen(item, false)
      }
    })
  }

  function bindDesktopMenus() {
    var items = document.querySelectorAll(
      '.main-menu .has-sub-nav, .main-menu .has-big-nav, .right-nav .has-sub-nav'
    )
    items.forEach(function (item) {
      var trigger = getDesktopMenuTrigger(item)
      var panel = getDesktopMenuPanel(item)
      if (!trigger || !panel || trigger.dataset.iwMenuBound) return
      trigger.dataset.iwMenuBound = '1'

      if (panel.id) {
        trigger.setAttribute('aria-controls', panel.id)
      } else if (!panel.id && !trigger.getAttribute('aria-controls')) {
        var generatedId =
          'SiteNavPanel-dyn-' + Math.random().toString(36).slice(2, 9)
        panel.id = generatedId
        trigger.setAttribute('aria-controls', generatedId)
      }

      trigger.setAttribute(
        'aria-expanded',
        panel.classList.contains('active') ||
          item.classList.contains('iw-menu-open')
          ? 'true'
          : 'false'
      )
      trigger.setAttribute('aria-haspopup', 'true')

      // Live sync: hover (theme toggles .active) and keyboard (.iw-menu-open).
      var syncExpanded = function () {
        var open =
          item.classList.contains('iw-menu-open') ||
          panel.classList.contains('active')
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false')
        if (isDesktop()) setMenuPanelInert(panel, !open)
      }
      var panelObserver = new MutationObserver(syncExpanded)
      panelObserver.observe(panel, {
        attributes: true,
        attributeFilter: ['class']
      })
      var itemObserver = new MutationObserver(syncExpanded)
      itemObserver.observe(item, {
        attributes: true,
        attributeFilter: ['class']
      })
      // Closed mega/sub panels must not sit in the desktop tab order
      // (visibility:hidden alone is unreliable across browsers).
      if (isDesktop()) syncExpanded()

      trigger.addEventListener('keydown', function (e) {
        if (!isDesktop() && !item.classList.contains('has-sub-nav-account')) {
          return
        }
        var open = item.classList.contains('iw-menu-open')

        if (e.key === 'Escape' && open) {
          e.preventDefault()
          closeDesktopMenus()
          releasePanel(true)
          return
        }

        if (e.key === 'ArrowDown' && !open) {
          e.preventDefault()
          openDesktopMenu(item, trigger)
          return
        }

        if (e.key === ' ') {
          e.preventDefault()
          if (open) {
            closeDesktopMenus()
            releasePanel(false)
            trigger.focus()
          } else {
            openDesktopMenu(item, trigger)
          }
          return
        }

        // Mobile account: Enter goes to login / account (no flyout destination).
        if (
          e.key === 'Enter' &&
          !isDesktop() &&
          item.classList.contains('has-sub-nav-account')
        ) {
          var accountUrl =
            trigger.getAttribute('data-account-url') || '/account/login'
          e.preventDefault()
          window.location.href = accountUrl
          return
        }

        if (e.key === 'Enter' && !open) {
          e.preventDefault()
          openDesktopMenu(item, trigger)
        }
      })

      // Account is a button (no destination href) — toggle on click as well
      // as keyboard, so touch / non-hover pointer users can open it.
      // Main-menu accordion buttons on mobile are owned by the theme accordion
      // (_sm-theme.js); opening them as desktop disclosures steals the mobile
      // focus trap and re-walks the same submenu links after exit.
      if (trigger.tagName === 'BUTTON') {
        trigger.addEventListener('click', function (e) {
          if (!isDesktop() && !item.classList.contains('has-sub-nav-account')) {
            return
          }
          e.preventDefault()
          if (item.classList.contains('iw-menu-open')) {
            closeDesktopMenus()
            releasePanel(false)
            trigger.focus()
          } else {
            openDesktopMenu(item, trigger)
          }
        })
      }
    })
  }

  function bindAccordionAria() {
    document.querySelectorAll('.accordion-toggle').forEach(function (toggle) {
      var parent = toggle.parentElement
      var content =
        (parent &&
          parent.querySelector(
            ':scope > .accordion-content, :scope > .big-nav, :scope > .sub-nav'
          )) ||
        toggle.nextElementSibling
      if (
        content &&
        (content.classList.contains('accordion-content') ||
          content.classList.contains('big-nav') ||
          content.classList.contains('sub-nav'))
      ) {
        if (!content.id) {
          content.id =
            'SiteNavAccordion-' + Math.random().toString(36).slice(2, 9)
        }
        toggle.setAttribute('aria-controls', content.id)
        toggle.setAttribute('aria-haspopup', 'true')
      }

      toggle.setAttribute(
        'aria-expanded',
        toggle.classList.contains('active') ? 'true' : 'false'
      )
      if (toggle.tagName !== 'BUTTON' && !toggle.hasAttribute('role')) {
        toggle.setAttribute('role', 'button')
      }
      if (
        toggle.tagName !== 'BUTTON' &&
        toggle.tagName !== 'A' &&
        !toggle.hasAttribute('tabindex')
      ) {
        toggle.setAttribute('tabindex', '0')
      }

      var observer = new MutationObserver(function () {
        toggle.setAttribute(
          'aria-expanded',
          toggle.classList.contains('active') ? 'true' : 'false'
        )
      })
      observer.observe(toggle, { attributes: true, attributeFilter: ['class'] })
    })
  }

  function setAccordionPanelInert(content, inert) {
    if (!content) return
    if (inert) {
      content.setAttribute('inert', '')
    } else {
      content.removeAttribute('inert')
    }
    content
      .querySelectorAll(
        'a.sub-link, a.pre-title, .accordion-content-inner a, .sub-nav-links a, .big-nav-links > a'
      )
      .forEach(function (el) {
        if (inert) el.setAttribute('inert', '')
        else el.removeAttribute('inert')
      })
  }

  // Desktop: inert every closed mega/sub panel so submenu links are not
  // tabbable until hover or keyboard opens the panel.
  function syncDesktopMenuInert() {
    document
      .querySelectorAll(
        '.main-menu .has-sub-nav, .main-menu .has-big-nav, .right-nav .has-sub-nav'
      )
      .forEach(function (item) {
        var panel = getDesktopMenuPanel(item)
        if (!panel) return
        if (!isDesktop()) return
        var open =
          item.classList.contains('iw-menu-open') ||
          panel.classList.contains('active')
        setMenuPanelInert(panel, !open)
      })
  }

  function syncMobileAccordionInert(nav) {
    if (!nav || isDesktop()) return
    stripSubnavListTabStops(nav)
    nav
      .querySelectorAll(
        ':scope li > .accordion-content, :scope li > .big-nav, :scope li > .sub-nav'
      )
      .forEach(function (content) {
        var toggle =
          content.parentElement &&
          content.parentElement.querySelector(
            ':scope > .accordion-toggle, :scope > button.site-nav__link'
          )
        var open =
          (toggle && toggle.classList.contains('active')) ||
          (content.parentElement &&
            content.parentElement.classList.contains('active'))
        setAccordionPanelInert(content, !open)
      })
  }

  // `#navbarNavDropdown` is the mobile off-canvas drawer AND the desktop
  // middle-menu host. Only inert the whole drawer below the desktop
  // breakpoint — otherwise desktop top-level links become untabbable.
  function syncMobileNavInert(nav) {
    if (!nav) return
    if (isDesktop()) {
      nav.removeAttribute('inert')
      syncDesktopMenuInert()
      return
    }
    if (nav.classList.contains('active')) {
      nav.removeAttribute('inert')
      syncMobileAccordionInert(nav)
    } else {
      nav.setAttribute('inert', '')
    }
  }

  function observeMobileNav() {
    var nav = document.getElementById('navbarNavDropdown')
    var hamburger = document.getElementById('hamburger')
    if (!nav || !hamburger) return

    observeSubnavTabStopStrip(nav)

    if (!hamburger.hasAttribute('aria-controls')) {
      hamburger.setAttribute('aria-controls', 'navbarNavDropdown')
    }
    hamburger.setAttribute(
      'aria-expanded',
      nav.classList.contains('active') ? 'true' : 'false'
    )

    syncMobileNavInert(nav)

    var observer = new MutationObserver(function () {
      var open = nav.classList.contains('active')
      hamburger.setAttribute('aria-expanded', open ? 'true' : 'false')
      hamburger.setAttribute('aria-label', open ? 'Close menu' : 'Menu')
      if (open) {
        syncMobileNavInert(nav)
        if (!isDesktop()) {
          activatePanel({
            id: 'mobile-nav',
            container: nav,
            trigger: hamburger,
            extras: [hamburger],
            keyboardTrap: true,
            focusEl: getFocusables(nav)[0] || nav
          })
        }
      } else {
        if (!isDesktop()) {
          // Reset any open accordions so the next open starts clean.
          nav
            .querySelectorAll('.accordion-toggle.active')
            .forEach(function (t) {
              closeMobileAccordion(t)
            })
        }
        syncMobileNavInert(nav)
        if (activePanel && activePanel.id === 'mobile-nav') {
          releasePanel(true)
        }
      }
    })
    observer.observe(nav, { attributes: true, attributeFilter: ['class'] })

    // Keep nested accordion panels out of tab order while collapsed (mobile only).
    nav.addEventListener(
      'click',
      function (e) {
        if (isDesktop()) return
        var toggle = e.target.closest && e.target.closest('.accordion-toggle')
        if (!toggle || !nav.contains(toggle)) return
        // Theme accordion close animates for ~1s before dropping `.active`.
        syncMobileAccordionInert(nav)
        window.setTimeout(function () {
          syncMobileAccordionInert(nav)
        }, 50)
        window.setTimeout(function () {
          syncMobileAccordionInert(nav)
        }, 1600)
      },
      true
    )

    nav.querySelectorAll('.accordion-toggle').forEach(function (toggle) {
      var toggleObserver = new MutationObserver(function () {
        if (!isDesktop()) syncMobileAccordionInert(nav)
      })
      toggleObserver.observe(toggle, {
        attributes: true,
        attributeFilter: ['class']
      })
    })

    var resizeTimer = null
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(function () {
        syncMobileNavInert(nav)
        if (isDesktop() && activePanel && activePanel.id === 'mobile-nav') {
          releasePanel(false)
        }
      }, 100)
    })
  }

  function observeCart() {
    var sidebar = document.getElementById('CartSidebar')
    if (!sidebar) return

    sidebar.setAttribute('role', 'dialog')
    sidebar.setAttribute('aria-modal', 'true')
    if (!sidebar.getAttribute('aria-label')) {
      sidebar.setAttribute('aria-label', 'Cart')
    }

    document.querySelectorAll('.js__cart-expand').forEach(function (btn) {
      btn.setAttribute('aria-controls', 'CartSidebar')
      btn.setAttribute(
        'aria-expanded',
        sidebar.classList.contains('active') ? 'true' : 'false'
      )
    })

    document.querySelectorAll('.js__cart-close').forEach(function (btn) {
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'Close cart')
      }
    })

    if (!sidebar.classList.contains('active')) {
      sidebar.setAttribute('inert', '')
    }

    var observer = new MutationObserver(function () {
      var open = sidebar.classList.contains('active')
      document.querySelectorAll('.js__cart-expand').forEach(function (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false')
      })
      if (open) {
        sidebar.removeAttribute('inert')
        var closeBtn = sidebar.querySelector('.js__cart-close')
        activatePanel({
          id: 'cart',
          container: sidebar,
          trigger:
            document.querySelector('.js__cart-expand.active') ||
            document.querySelector('.js__cart-expand'),
          keyboardTrap: true,
          focusEl: closeBtn || getFocusables(sidebar)[0]
        })
      } else {
        sidebar.setAttribute('inert', '')
        if (activePanel && activePanel.id === 'cart') {
          releasePanel(true)
        }
      }
    })
    observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] })
  }

  function observeSearch() {
    var searches = document.querySelectorAll('iw-search, .iw-search')
    if (!searches.length) return

    searches.forEach(function (search) {
      search.setAttribute('role', 'dialog')
      search.setAttribute('aria-modal', 'true')
      if (!search.getAttribute('aria-label')) {
        search.setAttribute('aria-label', 'Search')
      }

      var closeBtn = search.querySelector('.js-search-close')
      if (closeBtn) {
        if (closeBtn.tagName !== 'BUTTON') {
          closeBtn.setAttribute('role', 'button')
          closeBtn.setAttribute('tabindex', '0')
        }
        if (!closeBtn.getAttribute('aria-label')) {
          closeBtn.setAttribute('aria-label', 'Close search')
        }
        closeBtn.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            closeBtn.click()
          }
        })
      }

      if (!search.classList.contains('active')) {
        search.setAttribute('inert', '')
      }

      var observer = new MutationObserver(function () {
        var anyOpen = !!document.querySelector('.iw-search.active')
        document.querySelectorAll('.js__search').forEach(function (toggle) {
          toggle.setAttribute('aria-expanded', anyOpen ? 'true' : 'false')
        })
        if (search.classList.contains('active')) {
          // Clear inert before isVisible — that helper treats [inert] as hidden,
          // so checking first would early-return and leave the open panel inert
          // (Tab never reaches the search input).
          search.removeAttribute('inert')
          // Header renders two iw-search nodes; trap the visible one.
          if (!isVisible(search)) return
          var input = search.querySelector('.instant-search__input')
          var trigger = getSearchTrigger(document.activeElement)
          activatePanel({
            id: 'search',
            container: search,
            trigger: trigger,
            keyboardTrap: true,
            focusEl: input || closeBtn || getFocusables(search)[0]
          })
        } else {
          search.setAttribute('inert', '')
          // Header mounts two iw-search nodes; opening toggles both, but the
          // X only closes its own host. Clear siblings so we don't leave a
          // lingering .active that blocks focus restore.
          document
            .querySelectorAll('iw-search.active, .iw-search.active')
            .forEach(function (el) {
              if (el !== search) el.classList.remove('active')
            })
          anyOpen = !!document.querySelector('.iw-search.active')
          document.querySelectorAll('.js__search').forEach(function (toggle) {
            toggle.setAttribute('aria-expanded', anyOpen ? 'true' : 'false')
          })
          if (activePanel && activePanel.id === 'search' && !anyOpen) {
            var restoreTrigger = activePanel.trigger
            releasePanel(false)
            focusSearchTrigger(restoreTrigger)
          }
        }
      })
      observer.observe(search, { attributes: true, attributeFilter: ['class'] })
    })

    document.querySelectorAll('.js__search').forEach(function (toggle) {
      if (!toggle.getAttribute('aria-label') && !toggle.getAttribute('title')) {
        toggle.setAttribute('aria-label', 'Search')
      }
      toggle.setAttribute('aria-expanded', 'false')
      toggle.setAttribute('aria-haspopup', 'dialog')
    })
  }

  function bindHoverMenuEscape() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || activePanel) return
      var openHover = document.querySelector(
        '.has-sub-nav > .sub-nav.active, .has-big-nav > .big-nav.active'
      )
      if (!openHover) return
      e.preventDefault()
      closeDesktopMenus()
    })
  }

  function neutralizeHashMenuTitles() {
    document
      .querySelectorAll(
        '.big-nav-links > a.sub-link, .big-nav-links > a.pre-title'
      )
      .forEach(function (link) {
        var href = (link.getAttribute('href') || '').trim()
        if (href !== '#' && href !== '/#' && href !== '') return
        link.removeAttribute('href')
        link.setAttribute('role', 'presentation')
        link.setAttribute('tabindex', '-1')
        link.addEventListener('click', function (e) {
          e.preventDefault()
        })
      })
  }

  function init() {
    bindDesktopMenus()
    bindAccordionAria()
    bindHoverMenuEscape()
    neutralizeHashMenuTitles()
    observeMobileNav()
    observeCart()
    observeSearch()
    syncDesktopMenuInert()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
